import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DijkstraEngine, { NodeId, WeightedEdge } from "../GraphEngine/DijkstraEngine";
import styles from "../Graph.module.css";

const ALPHABET: NodeId[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const DijkstraWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<NodeId[]>([]);
  const [edges, setEdges] = useState<WeightedEdge[]>([]);
  const [startNode, setStartNode] = useState<NodeId | null>(null);
  const [targetNode, setTargetNode] = useState<NodeId | null>(null);
  const [edgeFrom, setEdgeFrom] = useState<NodeId | "">("");
  const [edgeTo, setEdgeTo] = useState<NodeId | "">("");
  const [edgeWeight, setEdgeWeight] = useState<string>("");
  const [nextNodeIndex, setNextNodeIndex] = useState<number>(0);
  const [runSignal, setRunSignal] = useState(0);
  const [resetSignal, setResetSignal] = useState(0);
  const [highlightedNodes, setHighlightedNodes] = useState<NodeId[]>([]);
  const [prev, setPrev] = useState<Record<NodeId, NodeId | null>>({});
  const [, setDistances] = useState<Record<NodeId, number>>({});
  const [, setQueueSnapshot] = useState<NodeId[]>([]);
  const [showPseudo, setShowPseudo] = useState(false);

  const handleAddNode = () => {
    if (nextNodeIndex >= ALPHABET.length) return;
    const newNode = ALPHABET[nextNodeIndex];

    setNodes((prevNodes) => [...prevNodes, newNode]);
    setNextNodeIndex((i) => i + 1);

    if (!startNode) {
      setStartNode(newNode);
    }
    if (!targetNode && nodes.length > 0) {
      setTargetNode(nodes[0]);
    }
  };

  const handleAddEdge = () => {
    if (!edgeFrom || !edgeTo) return;
    if (edgeFrom === edgeTo) return;
    const weightValue = parseInt(edgeWeight, 10);
    if (Number.isNaN(weightValue) || weightValue <= 0) return;

    const [a, b] = edgeFrom < edgeTo ? [edgeFrom, edgeTo] : [edgeTo, edgeFrom];

    const exists = edges.some(
      (e) =>
        (e.from === a && e.to === b) ||
        (e.from === b && e.to === a)
    );
    if (exists) return;

    setEdges((prevEdges) => [
      ...prevEdges,
      { from: a, to: b, weight: weightValue },
    ]);

    setEdgeWeight("");
  };

  const handleGenerateGraph = () => {
    const baseNodes = ALPHABET.slice(0, 5);
    const chainEdges: WeightedEdge[] = [];

    for (let i = 0; i < baseNodes.length - 1; i++) {
      chainEdges.push({
        from: baseNodes[i],
        to: baseNodes[i + 1],
        weight: 1 + Math.floor(Math.random() * 9),
      });
    }

    const possible: Array<[NodeId, NodeId]> = [];
    for (let i = 0; i < baseNodes.length; i++) {
      for (let j = i + 1; j < baseNodes.length; j++) {
        const from = baseNodes[i];
        const to = baseNodes[j];
        if (
          !chainEdges.some(
            (e) =>
              (e.from === from && e.to === to) ||
              (e.from === to && e.to === from)
          )
        ) {
          possible.push([from, to]);
        }
      }
    }

    const shuffled = [...possible].sort(() => Math.random() - 0.5);
    const extraCount = 2 + Math.floor(Math.random() * 2); // 2–3 extra
    const extraEdges: WeightedEdge[] = shuffled.slice(0, extraCount).map(
      ([from, to]) => ({
        from,
        to,
        weight: 1 + Math.floor(Math.random() * 9),
      })
    );

    const allEdges = [...chainEdges, ...extraEdges];

    setNodes(baseNodes);
    setEdges(allEdges);
    setNextNodeIndex(5);
    setStartNode(baseNodes[0]);
    setTargetNode(baseNodes[baseNodes.length - 1]);
    setHighlightedNodes([]);
    setPrev({});
    setDistances({});
    setQueueSnapshot([]);
    setResetSignal((s) => s + 1);
  };

  const handleClearGraph = () => {
    setNodes([]);
    setEdges([]);
    setStartNode(null);
    setTargetNode(null);
    setNextNodeIndex(0);
    setHighlightedNodes([]);
    setPrev({});
    setDistances({});
    setQueueSnapshot([]);
    setResetSignal((s) => s + 1);
  };

  const handleRunDijkstra = () => {
    if (!startNode || nodes.length === 0) return;
    setHighlightedNodes([]);
    setPrev({});
    setDistances({});
    setQueueSnapshot([]);
    setRunSignal((s) => s + 1);
  };

  const handleReset = () => {
    setHighlightedNodes([]);
    setPrev({});
    setDistances({});
    setQueueSnapshot([]);
    setResetSignal((s) => s + 1);
  };

  const shortestPathNodes: NodeId[] = useMemo(() => {
    if (!startNode || !targetNode || !prev || Object.keys(prev).length === 0) {
      return [];
    }

    const path: NodeId[] = [];
    let current: NodeId | null = targetNode;
    const guard = new Set<NodeId>();

    while (current !== null && !guard.has(current)) {
      guard.add(current);
      path.push(current);
      if (current === startNode) break;
      current = prev[current] ?? null;
    }

    if (path[path.length - 1] !== startNode) {
      return [];
    }

    return path.reverse();
  }, [startNode, targetNode, prev]);

  const adjacencyList = useMemo(() => {
    const adj: Record<NodeId, Array<{ node: NodeId; weight: number }>> = {};
    nodes.forEach((n) => (adj[n] = []));

    edges.forEach(({ from, to, weight }) => {
      if (!adj[from]) adj[from] = [];
      if (!adj[to]) adj[to] = [];
      adj[from].push({ node: to, weight });
      adj[to].push({ node: from, weight });
    });

    return adj;
  }, [nodes, edges]);

  const adjacencyMatrix = useMemo(() => {
    const index: Record<NodeId, number> = {};
    nodes.forEach((n, i) => {
      index[n] = i;
    });

    const size = nodes.length;
    const matrix: number[][] = Array.from({ length: size }, () =>
      Array(size).fill(0)
    );

    edges.forEach(({ from, to, weight }) => {
      const i = index[from];
      const j = index[to];
      if (i === undefined || j === undefined) return;
      matrix[i][j] = weight;
      matrix[j][i] = weight;
    });

    return matrix;
  }, [nodes, edges]);

  return (
    <div className={styles.graphPageContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>Dijkstra Shortest Path (Undirected Weighted Graph)</h1>
      <p className={styles.description}>
        Build an <strong>undirected weighted graph</strong> and visualize{" "}
        <strong>Dijkstra&apos;s shortest path algorithm</strong>. Edge weights
        represent cost or distance, and Dijkstra always expands the node with
        the <strong>smallest known distance</strong> from the start node.
      </p>

      <div className={styles.useCase}>
        <h3>Use Cases</h3>
        <p>
          Dijkstra is used in <strong>routing</strong>,{" "}
          <strong>GPS navigation</strong>,{" "}
          <strong>network latency optimization</strong>, and anywhere you want
          a <strong>least-cost path</strong> through a graph.
        </p>
        <p>
          Note: for BFS/DFS, a weighted graph behaves just like a normal
          undirected graph because those algorithms ignore weights. Here we
          use weights only for <strong>shortest-path computation</strong>.
        </p>
      </div>

      <div className={styles.timeComplexity}>
        <h3>Time Complexity</h3>
        <p>
          With a simple array-based priority queue, Dijkstra runs in{" "}
          <strong>O(V²)</strong>. With a binary heap, it improves to{" "}
          <strong>O((V + E) log V)</strong>, where V is the number of nodes and
          E is the number of edges.
        </p>
      </div>

      <div className={styles.graphControlsRow}>
        <div className={styles.graphControlsGroup}>
          <h3>Build Graph</h3>

          <button onClick={handleAddNode}>Add Node</button>
          <button onClick={handleGenerateGraph}>Generate Random Graph</button>
          <button onClick={handleClearGraph}>Clear Graph</button>

          <p className={styles.controlsNote}>
            Add nodes labeled A, B, C… and connect them with{" "}
            <strong>weighted edges</strong>. The random generator creates a
            connected graph with random positive weights between 1 and 10.
          </p>

          <h4 style={{ marginTop: "0.75rem" }}>Add Edge (Undirected, With Weight)</h4>

          {nodes.length < 2 ? (
            <p className={styles.emptyState}>
              Add at least two nodes to start creating edges.
            </p>
          ) : (
            <div className={styles.inlineForm}>
              <label>
                From:
                <select
                  value={edgeFrom}
                  onChange={(e) => setEdgeFrom(e.target.value as NodeId)}
                >
                  <option value="">—</option>
                  {nodes.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                To:
                <select
                  value={edgeTo}
                  onChange={(e) => setEdgeTo(e.target.value as NodeId)}
                >
                  <option value="">—</option>
                  {nodes.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Weight:
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={edgeWeight}
                  onChange={(e) => setEdgeWeight(e.target.value)}
                  style={{ width: "4rem" }}
                />
              </label>

              <button onClick={handleAddEdge}>Add Edge</button>
            </div>
          )}
        </div>

        <div className={styles.graphControlsGroup}>
          <h3>Dijkstra Controls</h3>

          <div className={styles.pseudocodeContainer}>
            <button
              className={styles.pseudocodeToggle}
              onClick={() => setShowPseudo((prev) => !prev)}
            >
              {showPseudo
                ? "▼ Hide Dijkstra Pseudocode"
                : "▶ Show Dijkstra Pseudocode"}
            </button>

            {showPseudo && (
              <pre className={styles.pseudocodeBlock}>
            {`Dijkstra(start):
            for each node v:
                dist[v] = ∞
                prev[v] = null
            dist[start] = 0

            Q = all nodes

            while Q not empty:
                u = node in Q with smallest dist[u]
                remove u from Q

                for each neighbor v of u:
                    alt = dist[u] + weight(u, v)
                    if alt < dist[v]:
                        dist[v] = alt
                        prev[v] = u`}
              </pre>
            )}
          </div>

          {nodes.length === 0 ? (
            <p className={styles.emptyState}>
              Add nodes or generate a graph to run Dijkstra.
            </p>
          ) : (
            <>
              <div className={styles.inlineForm}>
                <label>
                  Start Node:
                  <select
                    value={startNode ?? ""}
                    onChange={(e) =>
                      setStartNode(e.target.value as NodeId)
                    }
                  >
                    {nodes.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Highlight Path To:
                  <select
                    value={targetNode ?? ""}
                    onChange={(e) =>
                      setTargetNode(e.target.value as NodeId)
                    }
                  >
                    {nodes.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <button onClick={handleRunDijkstra}>Run Dijkstra</button>
              <button onClick={handleReset}>Reset</button>

              <p className={styles.controlsNote}>
                Dijkstra always chooses the{" "}
                <strong>next node with the smallest known distance</strong>{" "}
                from the start node. The animation shows which nodes are
                visited and how the final shortest path is formed.
              </p>
            </>
          )}

          <p className={styles.traversalResult}>
            Shortest path from {startNode ?? "—"} to {targetNode ?? "—"}:{" "}
            {shortestPathNodes.length
              ? shortestPathNodes.join(" → ")
              : "No path found yet or Dijkstra has not completed."}
          </p>
        </div>
      </div>

      <div className={styles.graphCanvasCard}>
        <DijkstraEngine
          nodes={nodes}
          edges={edges}
          startNode={startNode}
          runSignal={runSignal}
          resetSignal={resetSignal}
          highlightedNodes={highlightedNodes}
          shortestPathNodes={shortestPathNodes}
          setHighlightedNodes={setHighlightedNodes}
          setDistances={setDistances}
          setPrev={setPrev}
          setQueueSnapshot={setQueueSnapshot}
        />
      </div>

      <div className={styles.graphRepRow}>
        <div className={styles.graphRepCard}>
          <h3>Adjacency List (With Weights)</h3>
          {nodes.length === 0 ? (
            <p className={styles.emptyState}>
              No nodes yet. Add nodes or generate a graph to see the adjacency list.
            </p>
          ) : (
            <pre className={styles.codeBlock}>
              {nodes
                .map((n) => {
                  const neighbors = adjacencyList[n] ?? [];
                  const line = neighbors.length
                    ? neighbors
                        .map((entry) => `${entry.node}(${entry.weight})`)
                        .join(", ")
                    : "—";
                  return `${n}: ${line}`;
                })
                .join("\n")}
            </pre>
          )}
        </div>

        <div className={styles.graphRepCard}>
          <h3>Adjacency Matrix (Weights)</h3>
          {nodes.length === 0 ? (
            <p className={styles.emptyState}>
              No nodes yet. The adjacency matrix will appear once the graph has nodes and edges.
            </p>
          ) : (
            <table className={styles.matrixTable}>
              <thead>
                <tr>
                  <th></th>
                  {nodes.map((n) => (
                    <th key={n}>{n}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {adjacencyMatrix.map((row, i) => (
                  <tr key={nodes[i]}>
                    <th>{nodes[i]}</th>
                    {row.map((val, j) => (
                      <td key={j}>{val === 0 ? "0" : val}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DijkstraWrapper;
